import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Calendar, ArrowRight, Loader2, Clock, Sparkles } from 'lucide-react';
import { Link } from 'react-router';

interface DashboardStats {
    event: {
        id: number;
        name: string;
        startDate: string;
        endDate: string;
    } | null;
}

export const RetreatUpcomingWidget = () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3100/api';

    const { data, isLoading } = useQuery<DashboardStats>({
        queryKey: ['retreat-dashboard-stats'],
        queryFn: async () => {
            const res = await axios.get(`${apiUrl}/retreat/dashboard/stats`, { withCredentials: true });
            return res.data;
        },
    });

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-primary" />
            </div>
        );
    }

    if (!data?.event) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 p-4 text-center">
                <Sparkles className="mb-2 opacity-50 text-gold-500" size={32} />
                <p className="text-sm font-medium">Kein kommendes Event</p>
            </div>
        );
    }

    const startDate = new Date(data.event.startDate);
    const endDate = new Date(data.event.endDate);
    const now = new Date();

    const diffTime = startDate.getTime() - now.getTime();
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const isOngoing = now >= startDate && now <= endDate;

    return (
        <div className="h-full flex flex-col justify-between space-y-4">
            <div className="space-y-4">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gold-50 flex items-center justify-center text-gold-600 shadow-xs border border-gold-100/50">
                        <Calendar size={24} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="font-bold text-charcoal truncate" title={data.event.name}>
                            {data.event.name}
                        </h4>
                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-400 mt-0.5">
                            <Clock size={12} className="text-gray-300" />
                            {startDate.toLocaleDateString('de-DE')} - {endDate.toLocaleDateString('de-DE')}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 bg-white/40 p-3 rounded-xl border border-white/60 shadow-inner">
                    {isOngoing ? (
                        <>
                            <div className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                            </div>
                            <span className="text-sm font-bold text-green-700">Findet gerade statt</span>
                        </>
                    ) : (
                        <>
                            <div className="flex -space-x-1">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="w-5 h-5 rounded-full bg-gold-200 border-2 border-white flex items-center justify-center text-[8px] font-black text-white">
                                        {i === 3 ? '+' : ''}
                                    </div>
                                ))}
                            </div>
                            <div className="text-sm font-bold text-charcoal">
                                Startet in <span className="text-primary">{daysUntil} Tagen</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Link
                to="/retreat/events?create=true"
                className="flex items-center justify-between px-4 py-3 bg-charcoal text-white rounded-xl text-sm font-bold hover:bg-charcoal/90 transition-all group shadow-lg shadow-charcoal/10"
            >
                Event planen
                <ArrowRight size={16} className="text-white/50 group-hover:text-white transition-all group-hover:translate-x-1" />
            </Link>
        </div>
    );
};
